import { Component, computed, effect, inject, linkedSignal, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { catchError, finalize, firstValueFrom, map, of } from 'rxjs';
import { form, FormRoot } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { LoaderService, ModalUiService, SnackbarService } from '@dss-libs/shared/utility/common-services';
import { OrdersService } from '@dss-libs/portal/data-access/contract-order';
import { TabComponent, TabsGroupComponent } from '@dss-libs/portal/ui/portal-common-components';
import { ConfirmationModalComponent, UploadFileComponent } from '@dss-libs/shared/ui/common-components';
import { EventTypeEnum, TConfirmationModalData } from '@dss-libs/shared/type/common-types';
import { TranslocoDirective } from '@jsverse/transloco';
import { OrderMessageComponent } from './order-message/order-message.component';
import { OrderSettingsComponent } from './order-settings/order-settings.component';
import { RecipientInformationFormComponent } from './recipient-information-form/recipient-information-form.component';
import { SubscriptionsService } from '@dss-libs/portal/data-access/payment';
import { MatIcon } from '@angular/material/icon';
import { ICreateOrderFormModel, ReminderInterval } from '@dss-libs/portal/type/portal-types';
import { createInitialCreateOrderModel } from './forms/create-order-form.factory';
import { createOrderFormSchema } from './forms/create-order-form.schema';
import { isSignerAuthenticationRequired } from './forms/order-settings.utils';
import { mapCreateOrderFormToFormData } from './forms/create-order-form.mapper';

const RECIPIENT_INFORMATION_TAB_INDEX = 0;

@Component({
  selector: 'dss-libs-feature-create-order',
  imports: [
    TranslocoDirective,
    UploadFileComponent,
    FormRoot,
    TabsGroupComponent,
    TabComponent,
    RecipientInformationFormComponent,
    OrderSettingsComponent,
    OrderMessageComponent,
    MatIcon,
    NgTemplateOutlet,
  ],
  templateUrl: './create-order.component.html',
  styleUrl: './create-order.component.css',
})
export class CreateOrderComponent {
  readonly #router = inject(Router);
  readonly #modalService = inject(ModalUiService);
  readonly #snackbarService = inject(SnackbarService);
  readonly #contractOrderService = inject(OrdersService);
  readonly #subscriptionsService = inject(SubscriptionsService);
  readonly #loadingService = inject(LoaderService);

  expirationPeriodList: number[] = [30, 60, 90];
  reminderIntervalList: ReminderInterval[] = [
    ReminderInterval.Never,
    ReminderInterval.Daily,
    ReminderInterval.TwiceAWeek,
    ReminderInterval.Weekly,
    ReminderInterval.ADayBeforeExpiration,
  ];

  readonly signingMethodsResource = this.#contractOrderService.signingMethodsResource();
  readonly signingMethodsList = computed(() =>
    this.signingMethodsResource.hasValue() ? this.signingMethodsResource.value() : []
  );
  readonly subscriptionSignaturesResource = this.#subscriptionsService.subscriptionSignaturesResource();
  readonly subscriptionSignatures = computed(() =>
    this.subscriptionSignaturesResource.hasValue() ? this.subscriptionSignaturesResource.value() : undefined
  );

  protected readonly showInsufficientSignaturesBanner = linkedSignal(() => {
    const signatures = this.subscriptionSignatures();
    return !!signatures && signatures.remainingSignatures <= signatures.pendingSignatures;
  });

  readonly uploadedDocuments = signal<FormData | null>(null);
  readonly createOrderModel = signal<ICreateOrderFormModel>(createInitialCreateOrderModel());
  readonly createOrderForm = form(this.createOrderModel, createOrderFormSchema(this.signingMethodsList), {
    submission: {
      action: (field) => this.#createOrder(field().value()),
      onInvalid: () => this.#invalidTabIndex.set(this.#firstInvalidTabIndex()),
    },
  });

  readonly #invalidTabIndex = signal<number | undefined>(undefined);
  readonly #firstInvalidTabIndex = computed(() => {
    const { recipientInformation, orderSettings, orderMessage } = this.createOrderForm;
    const index = [recipientInformation, orderSettings, orderMessage].findIndex((section) => section().invalid());
    return index === -1 ? undefined : index;
  });

  readonly tabIndexWithValidationError = computed(() => {
    const invalidTabIndex = this.#invalidTabIndex();
    const isRecipientInformationFixed =
      invalidTabIndex === RECIPIENT_INFORMATION_TAB_INDEX && !this.createOrderForm.recipientInformation().invalid();
    return isRecipientInformationFixed ? undefined : invalidTabIndex;
  });

  readonly #requiresSignerAuthentication = computed(() =>
    isSignerAuthenticationRequired(
      this.signingMethodsList(),
      this.createOrderForm.orderSettings.signingMethod().value()
    )
  );

  constructor() {
    effect(() => {
      const isRequired = this.#requiresSignerAuthentication();
      untracked(() => this.createOrderForm.orderSettings.requireSignerAuthentication().value.set(isRequired));
    });
  }

  filesUploadedListEvent(fileList: FormData) {
    this.uploadedDocuments.set(fileList);
    this.createOrderForm.documents.count().value.set(Array.from(fileList.values()).length);
  }

  onDiscardTriggered() {
    const orderState = this.createOrderForm();
    if (!orderState.dirty() && !orderState.touched()) {
      void this.#router.navigate(['/orders']);
      return;
    }

    const data: TConfirmationModalData = {
      svgIcon: 'danger-triangle-outline',
      iconVariant: 'danger',
      title: 'CONTRACT_ORDER.DISCARD_ORDER_TITLE',
      subtitle: 'CONTRACT_ORDER.DISCARD_ORDER_SUBTITLE',
      message: 'CONTRACT_ORDER.DISCARD_ORDER_DESCRIPTION',
      primaryButtonLabel: 'CONTRACT_ORDER.DISCARD_ORDER',
      primaryButtonColor: 'danger',
      secondaryButtonLabel: 'CONTRACT_ORDER.KEEP_EDITING',
    };
    this.#modalService.openAbstractModal({
      component: ConfirmationModalComponent,
      dialogData: data,
      onSubmit: () => this.#router.navigate(['/orders']),
    });
  }

  async #createOrder(order: ICreateOrderFormModel): Promise<void> {
    this.#invalidTabIndex.set(undefined);
    this.#loadingService.showLoader();

    const isCreated = await firstValueFrom(
      this.#contractOrderService.createOrder(mapCreateOrderFormToFormData(order, this.uploadedDocuments())).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(() => this.#loadingService.hideLoader())
      )
    );

    if (!isCreated) return;
    this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'VALIDATION_MESSAGES.CONTRACT_CREATED');
    void this.#router.navigate(['/orders']);
  }
}
